import { Box, IconButton, MenuItem, Select, SelectChangeEvent, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

interface MobileTablePaginationProps {
  total: number;
  pageSize: number;
  pageSizeOptions?: number[];
  page: number; // 0-based
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

// Shared pager for this module's horizontally-scrollable mobile tables
// (Performance's Account Summary, Brands' Daily/MTD): a rows-per-page
// picker plus prev/next. Rendered by the caller BELOW the horizontally-
// scrolling table box, not inside it - it used to live inside that box with
// `position: sticky, left: 0`, but that box's own width stretches to the
// full (unscrolled) table width, so `justify-content: space-between` was
// pushing the prev/next buttons off to the far right of the whole table,
// only reachable by scrolling all the way over. Sitting outside the scroll
// box, its width matches the card instead, so it's always fully visible.
export default function MobileTablePagination({
  total,
  pageSize,
  pageSizeOptions = [5, 10, 25, 50],
  page,
  onPageChange,
  onPageSizeChange,
}: MobileTablePaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to = Math.min((page + 1) * pageSize, total);

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    onPageSizeChange(Number(event.target.value));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, p: "10px 12px 2px" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Typography sx={{ fontSize: 11.5, color: "#9e9e9e" }}>Rows per page</Typography>
          <Select
            size="small"
            value={pageSize}
            onChange={handlePageSizeChange}
            color="secondary"
            sx={{ fontSize: 11.5, height: 26, minWidth: 52, ".MuiSelect-select": { py: "2px", px: "8px" } }}
          >
            {pageSizeOptions.map((option) => (
              <MenuItem key={option} value={option} sx={{ fontSize: 12.5 }}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Typography sx={{ fontSize: 11.5, color: "#9e9e9e" }}>
          {from}-{to} of {total}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
        <IconButton size="small" onClick={() => onPageChange(page - 1)} disabled={page <= 0} sx={{ p: 0.5 }}>
          <ChevronLeftIcon sx={{ fontSize: 18, color: page <= 0 ? "#d0d0d0" : "#8E24AA" }} />
        </IconButton>
        <Typography sx={{ fontSize: 11.5, color: "#9e9e9e", minWidth: 60, textAlign: "center" }}>
          Page {page + 1} of {totalPages}
        </Typography>
        <IconButton size="small" onClick={() => onPageChange(page + 1)} disabled={page >= totalPages - 1} sx={{ p: 0.5 }}>
          <ChevronRightIcon sx={{ fontSize: 18, color: page >= totalPages - 1 ? "#d0d0d0" : "#8E24AA" }} />
        </IconButton>
      </Box>
    </Box>
  );
}
